import * as types from './actionTypes';
import { closeBackDropOverlay, openBackdropOverlay } from './apiStatusActions';
import { openErrorSnackBar, openSuccessSnackBar } from './snackBarActions';
import { logAndReturnErrorMsg } from '../../Utils/ErrorLog';
import { manageAssetDetailsRetrieveErrorMsg } from '../../Constants/ErrorMessages';
import {
	manageAssetLinkProductErrorMsg,
	manageAssetLinkProductSuccessMsg,
	productJsonProps,
} from '../../Constants/Products';
import { Results_NavLink_To } from '../../Constants/AppBar';
import { artifactStatusMap, fileNameLenWarningMsg, sourceFileNameLenLimit } from '../../Constants/Files';
import { assetEsUrl, assetsUrlPath, dataLoaderUrl, esApiKey } from '../../Constants/Api';
import { forEach, get, isEmpty, isNil } from 'lodash';
import axios from 'axios';
import { openAlertDialog } from './dialogActions';
import { fetchManageProductByUUID } from './manageProductActions';
import { AlertDialogProps } from '../../Utils/DialogUtils';

// constants
const publishInfo = `The approved artifact will automatically published to lowes.com if it met these requirements:
1. The artifact belongs to a primary model.
2. The asset had an associated product.`;

export function dispatchActionData(type, data) {
	return { type: type, data: data };
}
//action creator for getting asset data by uuid
export function fetchAssetDataByUUID(data) {
	return async function (dispatch) {
		dispatch(openBackdropOverlay('Fetching asset details...'));
		try {
			const assetDetails = await getAssetFromESById(data);
			dispatch(setManageAssetData(assetDetails));
		} catch (error) {
			dispatch(setManageAssetData({}));
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, manageAssetDetailsRetrieveErrorMsg)));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}

// set manage asset state data on manage asset page load.
export function setManageAssetData(data) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.FETCH_MANAGEASSET_DATA, data));
	};
}
// delete a single tag from an asset
export function deleteTagFromAsset(tag, asset) {
	return async function (dispatch) {
		try {
			// TODO: tags storing in assetTags and tags table should be consistent should follow same case.
			await axios.delete(`${assetsUrlPath}/${asset.id}/tags/${tag}`);
			await dispatch(dispatchActionData(types.MANAGE_ASSET_DELETE_TAG, tag));
			dispatch(openSuccessSnackBar('Tag removed from ' + asset.name));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'error removing tag from' + asset.name)));
		}
	};
}
// add a single tag to an asset
export function addTagToAsset(tag, asset) {
	return async function (dispatch) {
		try {
			// only add if the tag was not already selected
			const tagAlreadyExists = asset.tags.some((t) => t === tag);
			if (!tagAlreadyExists) {
				await axios.post(`${assetsUrlPath}/${asset.id}/tags`, { tag });
				await dispatch(dispatchActionData(types.MANAGE_ASSET_ADD_TAG, tag));
				dispatch(openSuccessSnackBar('Tag added to ' + asset.name));
			}
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'error adding tag from' + asset.name)));
		}
	};
}
//deleting all tags for manage assets
export function deleteAllTagsFromAsset(asset) {
	return async function (dispatch) {
		try {
			// collection list of all tags deletion promises for this asset.
			const delTagPromises = [];
			for (const tag of asset.tags) {
				if (tag) {
					const promise = await axios.delete(`${assetsUrlPath}/${asset.id}/tags/${tag}`);
					delTagPromises.push(promise);
				}
			}
			await Promise.all(delTagPromises);
			await dispatch(dispatchActionData(types.MANAGE_ASSET_DELETE_ALL_TAGS));
			dispatch(openSuccessSnackBar('All Tags from ' + asset.name + ' removed '));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'error removing tags from' + asset.name)));
		}
	};
}
// linking related product for an empty asset.
export function linkRelatedProduct(product, asset) {
	return async function (dispatch) {
		try {
			const assetId = asset.id;
			const productEsId = get(product, productJsonProps.id);
			await axios.put(`${assetsUrlPath}/${assetId}/products/${productEsId}`);
			await dispatch(dispatchActionData(types.MANAGE_ASSET_LINK_PRODUCT, product));
			dispatch(openSuccessSnackBar(manageAssetLinkProductSuccessMsg + asset.name));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, manageAssetLinkProductErrorMsg + asset.name)));
		}
	};
}
// un stage all product/products for this asset
/**
TODO:e.DELETE("/v1/assets/:id/products", d.unstageProductsHandler) removes all products for an asset from staged_products table.
using this endpoint to un-stage a product, when user removes the staged product from autocomplete search.
it is safe to use this endpoint since asset <-> product has only 1:1 relation in this table.
 */
export function unStageProductFromAsset(asset, product) {
	return async function (dispatch) {
		try {
			await axios.delete(`${assetsUrlPath}/${asset.id}/products/${product.id}`);
			await dispatch(dispatchActionData(types.MANAGE_ASSET_LINK_PRODUCT, {}));
			dispatch(openSuccessSnackBar(`product unlinked from asset ${asset.name}`));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, `error unlinking product from asset${asset.name}`)));
		}
	};
}

// removing asset from manage asset page
export function removeAsset(asset, history, searchTerm) {
	return async function (dispatch) {
		try {
			await dispatch(dispatchActionData(types.MANAGE_ASSET_DELETE_ASSET));
			history.push(Results_NavLink_To + '/' + searchTerm);
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error removing asset' + asset.name)));
		}
	};
}
// adding files to asset
export function addFilesToAsset(asset, files) {
	return async function (dispatch) {
		dispatch(openBackdropOverlay('Uploading File(s) and Generating Artifacts..'));
		try {
			console.log('uploading files', files);
			const fileUploadPromises = [];
			// using lodash forEach for iterating over fileList since it is not an array.
			forEach(files, (srcFile) => {
				// Skip the source file and throw error. if the file name is greater than 100 char.
				if (srcFile.name.length > sourceFileNameLenLimit) {
					dispatch(openErrorSnackBar(fileNameLenWarningMsg));
					return;
				}
				const formData = new FormData();
				formData.append('file', srcFile);
				const promise = axios.post(`${assetsUrlPath}/${asset.id}/files`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
				fileUploadPromises.push(promise);
			});
			// resolving all promises.
			const response = await Promise.all(fileUploadPromises);
			const uploadedSrcFiles = response.map((srcFile) => srcFile.data);
			console.log('uploaded files', uploadedSrcFiles);
			await dispatch(dispatchActionData(types.MANAGE_ASSET_ADD_FILES, uploadedSrcFiles));
			// re-fetch data from ES for updated signed-urls
			await dispatch(fetchAssetDataByUUID(asset.id));
			if (uploadedSrcFiles.length > 0) {
				dispatch(openSuccessSnackBar('Source File(s) added to ' + asset.name));
			}
		} catch (error) {
			console.log(error);
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error adding source file to ' + asset.name)));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}
export function addFileToArtifact(artifact, file) {
	return async function (dispatch) {
		dispatch(openBackdropOverlay('Uploading File and Overwriting Artifact..'));
		try {
			console.log('uploading file', file);
			const formData = new FormData();
			formData.append('file', file);
			await axios.post(`${assetsUrlPath}/${artifact.assetId}/files/${artifact.sourceFileId}/artifacts/${artifact.id}/data`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
			await dispatch(fetchAssetDataByUUID(artifact.assetId));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Could not overwrite artifact with file:' + file.name )));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}

// changing artifact model status
export function changeArtifactStatus({ asset, sourceFile, artifact, status }) {
	return async function (dispatch) {
		try {
			// throw an alignment required warning dialog. before switching artifact status to approved, if alignment is empty.
			if (
				sourceFile.isPrimary &&
				status === artifactStatusMap.approved.value &&
				!isNil(asset.alignment) &&
				isEmpty(asset.alignment)
			) {
				dispatch(
					openAlertDialog(
						new AlertDialogProps(
							'Alignment is required. before changing artifact status to approved.',
							'Alignment required',
						),
					),
				);
				return;
			}
			const url = `${assetsUrlPath}/${asset.id}/files/${sourceFile.id}/artifacts/${artifact.id}/approved`;
			let isApproved = false;
			if (status === artifactStatusMap.approved.value) {
				isApproved = true;
			}
			var data = JSON.stringify(isApproved);
			var config = {
				method: 'put',
				url: url,
				headers: {
					'Content-Type': 'application/json',
				},
				data: data,
			};
			const res = await axios(config);
			// Display publish info dialog, once artifact status has been selected as approved.
			if (status === artifactStatusMap.approved.value) {
				dispatch(
					openAlertDialog({
						title: 'Publish Info',
						content: publishInfo,
						size: 'sm',
					}),
				);
			}
			if (res.data) {
				const statusLabel = artifactStatusMap[status].label;
				const fileObj = {
					sourceFile,
					artifact,
					status: res.data,
				};
				dispatch(dispatchActionData(types.MANAGE_ASSET_CHANGE_ARTIFACT_STATUS, fileObj));
				dispatch(openSuccessSnackBar(artifact.type + ` status is set to  "${statusLabel}"`));
			}
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error changing artifact status')));
		}
	};
}
// removing a specific file from manage asset
export function removeSourceFileFromAsset(file) {
	return async function (dispatch) {
		try {
			// TODO: make a API request to endpoint to remove the individual file.
			await dispatch(dispatchActionData(types.MANAGE_ASSET_REMOVE_FILE, file));
			dispatch(openSuccessSnackBar(file.name + ' source file removed  '));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, ' Error removing file ' + file.name)));
		}
	};
}
// Making a model file as primary on manage asset page.
export function setModelFileAsPrimary(file, asset) {
	return async function (dispatch) {
		try {
			const url = `${assetsUrlPath}/${asset.id}/files/${file.id}/primary`;
			await axios.put(url, {});
			await dispatch(dispatchActionData(types.MANAGE_ASSET_SET_PRIMARY_FILE, file));
			dispatch(openSuccessSnackBar(`source file ${file.name} set as Primary model for asset ${asset.name}`));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, ' Error setting primary model file ' + file.name)));
		}
	};
}
// Rebuild a sourceFile
export function rebuildSourceFile(file, asset) {
	return async function (dispatch) {
		try {
			dispatch(openBackdropOverlay('Rebuilding Source File..'));
			const url = `${assetsUrlPath}/${asset.id}/files/${file.id}/build`;
			const res = await axios.post(url);
			if (res.data) {
				await dispatch(dispatchActionData(types.MANAGE_ASSET_REBUILD_SOURCE_FILE, res.data));
				dispatch(openSuccessSnackBar('source file (re)build succeeded'));
			}
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, ' Error building source model file ' + file.name)));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}
// Rebuild a sourceFile
export function rebuildArtifactFile(artifact) {
	return async function (dispatch) {
		try {
			dispatch(openBackdropOverlay('Rebuilding Artifact File..'));
			const url = `${assetsUrlPath}/${artifact.assetId}/files/${artifact.sourceFileId}/artifacts/${artifact.id}/build`;
			const res = await axios.post(url);
			if (res.data) {
				await dispatch(dispatchActionData(types.MANAGE_ASSET_REBUILD_ARTIFACT_FILE, res.data));
				dispatch(openSuccessSnackBar('artifact file (re)build succeeded'));
			}
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, ' Error building artifact type ' + artifact.type)));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}
// Update Asset level fields (Jira, name, metadata (alignment)).
export function updateAsset(asset) {
	return async function (dispatch) {
		try {
			if (asset.tags) {
				asset.tags = asset.tags.map((tag) => {
					return { tag: tag };
				});
			}
			const url = `${assetsUrlPath}/${asset.id}`;
			await axios.patch(url, asset);
			dispatch(openSuccessSnackBar('Asset has been updated'));
			// reloading the page, to avoid delay in getting updated asset from ES.
			window.location.reload();
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error Updating Asset')));
		}
	};
}
// Refresh Product data from Bifrost (which refreshes data on product ES index)
export function refreshProductFromBifrost(data) {
	const { productId, productUUID, assetId } = data;
	return async function (dispatch) {
		try {
			dispatch(openBackdropOverlay('Refreshing Product Data...'));
			const url = `${dataLoaderUrl}/bifrost/product/${productId}?refresh=true`;
			await axios.post(url);
			// if assetId is provided re-fetch asset info (i.e manage asset page).
			if (assetId) {
				await dispatch(fetchAssetDataByUUID(assetId));
			}
			// re-fetch product details when productUUID is provided (i.e product details page).
			if (productUUID) {
				await dispatch(fetchManageProductByUUID(productUUID));
			}
			dispatch(openSuccessSnackBar('Product data refreshed'));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, ' Error refreshing product')));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}

// Create Jira ticket
export function createJira({ assetId, productId }) {
	return async function (dispatch) {
		try {
			dispatch(openBackdropOverlay('Creating Jira Task...'));
			const url = `${assetsUrlPath}/${assetId}/jira`;
			const res = await axios.post(url, {
				productId,
			});
			if (res.data) {
				await dispatch(dispatchActionData(types.MANAGE_ASSET_UPDATE_JIRA_TICKET, 'Refresh'));
				dispatch(openSuccessSnackBar('Jira ticket job submitted successfully'));
			}
		} catch (error) {
			dispatch(
				openErrorSnackBar(logAndReturnErrorMsg(error, `Error creating Jira ticket for asset:  ${assetId}`)),
			);
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}

// retrieve asset by ID from asset index from ES
export const getAssetFromESById = (assetId) => {
	return axios
		.get(`${assetEsUrl}/_doc/${assetId}`, {
			headers: {
				Authorization: `ApiKey ${esApiKey}`,
			},
		})
		.then(async (response) => {
			const asset = response?.data?._source;
			const product = { ...asset.product };
			if (!isEmpty(product) && !isNil(product)) {
				delete asset.product;
				asset.product = {};
				// modifying asset product structure to be consistent with ES `product` index response.
				asset.product.id = product.id;
				asset.product.meta = product.meta;
				asset.product.product = { ...product };
			}
			return asset;
		})
		.catch((error) => {
			throw error;
		});
};
